import { getCurrentDateString } from "../types/datestring.types";
import { ingestAPIObject } from "../utils/api";
import { centerOfMass, distance, pointToPolygonDistance } from "@turf/turf";
export class PlantingDTO {
    constructor(jsonData) {
        ingestAPIObject(this, jsonData);
    }
    /**
     * "Current" properties
     * (Use actual if present, otherwise estimated)
     */
    // Dates
    get plantDateCurrent() {
        return this.plantDateActual ?? this.plantDate;
    }
    get transplantDateCurrent() {
        return this.transplantDateActual ?? this.transplantDate;
    }
    get establishedDate() {
        return this.plantDateCurrent ?? this.transplantDateCurrent;
    }
    get wetDateCurrent() {
        return this.wetDateActual ?? this.wetDate ?? getCurrentDateString();
    }
    get harvestDatesCurrent() {
        if (this.harvestDatesActual.length > 0) {
            return this.harvestDatesActual;
        }
        return this.harvestDates;
    }
    get endDateCurrent() {
        return this.endDateActual ?? this.endDate;
    }
    // Info
    get sublotCurrent() {
        return this.sublotActual ?? this.sublot;
    }
    set sublotCurrent(value) {
        if (this.isLive) {
            this.sublotActual = value;
        }
        else {
            this.sublot = value;
        }
    }
    get nurseryNameCurrent() {
        return this.nurseryNameActual ?? this.nurseryName;
    }
    get rotationCurrent() {
        return this.rotationActual ?? this.rotation;
    }
    get acresCurrent() {
        return this.acresActual ?? this.acres ?? 0;
    }
    set acresCurrent(value) {
        if (this.isLive) {
            this.acresActual = value;
        }
        else {
            this.acres = value;
        }
    }
    get commodityIdCurrent() {
        return this.commodityIdActual ?? this.commodityId;
    }
    get commodityCurrent() {
        return this.commodityNameActual ?? this.commodityName ?? "";
    }
    get varietyIdCurrent() {
        return this.commodityIdCurrent;
    }
    get varietyCurrent() {
        return this.varietyNameActual ?? this.varietyName ?? "";
    }
    /** Display name for the crop in "Commodity: Variety" format (or just Commodity if no variety) */
    get cropDisplayName() {
        const variety = this.varietyCurrent;
        return variety
            ? `${this.commodityCurrent}: ${variety}`
            : this.commodityCurrent;
    }
    // Bed Config
    get bedCountCurrent() {
        return this.bedCountActual ?? this.bedCount;
    }
    get bedSizeInchesCurrent() {
        return this.bedSizeInchesActual ?? this.bedSizeInches;
    }
    get bedLinesCurrent() {
        return this.bedLinesActual ?? this.bedLines;
    }
    get plantSpacingInchesCurrent() {
        return this.plantSpacingInchesActual ?? this.plantSpacingInches;
    }
    /** Current stage of the planting as far as we know */
    get status() {
        const now = getCurrentDateString();
        if (this.finalDate && this.finalDate < now)
            return PlantingStatus.Harvested;
        if (this.establishedDate && this.establishedDate < now)
            return PlantingStatus.Established;
        if (this.wetDateCurrent < now)
            return PlantingStatus.Active;
        return PlantingStatus.Upcoming;
    }
    get statusText() {
        switch (this.status) {
            case PlantingStatus.Upcoming:
                return "Upcoming";
            case PlantingStatus.Established:
                return "Planted";
            case PlantingStatus.Active:
                return "Active";
            case PlantingStatus.Harvested:
                return "Harvested";
        }
        return "";
    }
    /**
     * Do we think this planting is ongoing
     * (is it established, or at least past we date?)
     */
    get isLive() {
        return (this.status === PlantingStatus.Active ||
            this.status === PlantingStatus.Established);
    }
    /** Helper functions */
    distanceTo(point) {
        if (!this.latestBoundary || !Number.isFinite(point[0]))
            return undefined;
        const roughDistance = distance(centerOfMass(this.latestBoundary).geometry.coordinates, point);
        // If we're far away, just use the dist from the center
        if (roughDistance > 5) {
            return roughDistance;
        }
        return Math.max(0, pointToPolygonDistance(point, this.latestBoundary));
    }
}
export var PlantingStatus;
(function (PlantingStatus) {
    PlantingStatus[PlantingStatus["Upcoming"] = 0] = "Upcoming";
    PlantingStatus[PlantingStatus["Established"] = 1] = "Established";
    PlantingStatus[PlantingStatus["Active"] = 2] = "Active";
    PlantingStatus[PlantingStatus["Harvested"] = 3] = "Harvested";
})(PlantingStatus || (PlantingStatus = {}));
export var PlantMethod;
(function (PlantMethod) {
    PlantMethod[PlantMethod["Transplant"] = 1] = "Transplant";
    PlantMethod[PlantMethod["Seed"] = 2] = "Seed";
})(PlantMethod || (PlantMethod = {}));
export function plantMethodLabel(method) {
    return method !== undefined ? PlantMethodLabels[method] : "Unknown";
}
export const PlantMethodLabels = {
    [PlantMethod.Seed]: "Seed",
    [PlantMethod.Transplant]: "Transplant",
};
export var OrganicStatus;
(function (OrganicStatus) {
    OrganicStatus[OrganicStatus["Conventional"] = 1] = "Conventional";
    OrganicStatus[OrganicStatus["Organic"] = 2] = "Organic";
    OrganicStatus[OrganicStatus["Transitional"] = 3] = "Transitional";
})(OrganicStatus || (OrganicStatus = {}));
export const OrganicStatusLabels = {
    [OrganicStatus.Conventional]: "Conventional",
    [OrganicStatus.Organic]: "Organic",
    [OrganicStatus.Transitional]: "Transitional",
};
export function organicStatusLabel(status) {
    return status !== undefined ? OrganicStatusLabels[status] : "Unknown";
}
export var BoundaryType;
(function (BoundaryType) {
    BoundaryType[BoundaryType["Migrated"] = 0] = "Migrated";
    BoundaryType[BoundaryType["LotLevel"] = 1] = "LotLevel";
    BoundaryType[BoundaryType["GreenToGreen"] = 2] = "GreenToGreen";
    BoundaryType[BoundaryType["PlantingTicket"] = 3] = "PlantingTicket";
    BoundaryType[BoundaryType["FromRoughMap"] = 4] = "FromRoughMap";
    BoundaryType[BoundaryType["UserCreated"] = 5] = "UserCreated";
})(BoundaryType || (BoundaryType = {}));
